import { toLower } from "lodash";
import { isBooleanTrue, isListNotEmpty, isNumberNotEmpty, isStringNotEmpty } from "./objectUtils";

/*
 * Version labels are stored as strings, but a label typed as a number by a script or an import
 * arrives as a JS number and Firestore preserves the type on the way back out. A strict comparison
 * against the studio's strings would miss it and fall through to the default version, which looks
 * like it worked. Every comparison goes through here so both sides are the same shape.
 *
 * Normalization stays tolerant of any label shape even though authoring now only accepts numeric
 * ones - a label written before that rule has to keep resolving, not start resolving to nothing.
 */
export const getNormalizedSurveyVersion = (version) => {
	if (isNumberNotEmpty(version)) {
		return String(version);
	}

	// isStringNotEmpty is a not-null-or-blank check rather than a type check, which is how a number
	// reached the strict comparison in the first place - the number case above has to run first
	const normalizedVersion = isStringNotEmpty(version) ? String(version).trim() : '';

	return isStringNotEmpty(normalizedVersion) ? normalizedVersion : null;
};

/*
 * The key two labels are compared on, which is not the label itself. The marketing-site tracker
 * lowercases the ?v= it stores (public/readyrx.js) and the funnel re-stamps ?v= from that stored
 * value, so an alphabetic label authored before the numeric-only rule comes back in a case it was
 * never written in - and a strict comparison then serves the slug's default, silently, because
 * falling back to the default looks exactly like a match. Folding case was previously unsafe because
 * it would collide two authorable labels; it cannot now, since isValidSurveyVersionLabel admits only
 * digits and digits have no case. Nothing stores or displays this value - the label is reported as
 * it was authored.
 */
const getSurveyVersionMatchKey = (version) => {
	const normalizedVersion = getNormalizedSurveyVersion(version);

	return isStringNotEmpty(normalizedVersion) ? toLower(normalizedVersion) : null;
};

/*
 * Resolves a survey among versions sharing a slug. With a version (the ?v= query string value or a
 * flow item's pinned label), matches it exactly; without one, serves the default. Only active
 * surveys participate when the slug is versioned; surveys created before versioning (no
 * version/isDefault fields) are treated as the default for their slug.
 *
 * Test mode (?test=true) widens the exact-version match to inactive versions and NOTHING else - an
 * unpublished version is never promoted to the default of its slug, in either mode. Deactivation is
 * the only thing keeping a studio draft off a live slug: production runs a by-slug lookup with no
 * version awareness at all against this same database, so a draft that resolved for real visitors
 * here would be a draft production could serve to patients on document order alone.
 */
export const getSurveyBySlugAndVersion = (slug, version, surveys, isTestMode = false) => {
	if (!isListNotEmpty(surveys) || !isStringNotEmpty(slug)) {
		return null;
	}

	const family = surveys.filter(i => i.slug === slug);
	const candidates = family.filter(i => i.active !== false);
	const versionMatchKey = getSurveyVersionMatchKey(version);

	if (isStringNotEmpty(versionMatchKey)) {
		const versionCandidates = isBooleanTrue(isTestMode) ? family : candidates;
		return versionCandidates.find(i => getSurveyVersionMatchKey(i.version) === versionMatchKey) ?? null;
	}

	if (candidates.length === 0) {
		return null;
	}

	return candidates.find(i => i.isDefault !== false) ?? candidates[0];
};

/*
 * Authoring accepts whole numbers only. Digits are the one label shape that survives every hop of
 * the round trip - the tracker's lowercasing, the stamp back onto ?v=, a script writing the label as
 * a JS number - without a normalization step standing between what was authored and what is
 * compared. Keeping the rule is also what keeps the read side's case folding safe: two labels that
 * differ only in case would collide, and digits cannot differ only in case. Reading stays tolerant -
 * this gate is only ever asked about a label someone is typing.
 */
const NUMERIC_VERSION_LABEL = /^\d+$/;

export const isValidSurveyVersionLabel = (version) => {
	const normalizedVersion = getNormalizedSurveyVersion(version);

	return isStringNotEmpty(normalizedVersion) && NUMERIC_VERSION_LABEL.test(normalizedVersion);
};

/*
 * The version the funnel is actually serving, which is what analytics reports. The name has to
 * describe the funnel the customer walked, never the ?v= they arrived with - a stale or unmatched
 * request still serves the slug's default, so echoing it would report a funnel nobody saw. The
 * request is therefore ignored entirely and the label comes off the survey that resolved. A survey
 * authored before versioning carries no label and yields no version, which is honest: until the
 * backfill labels them, there is no version to report.
 */
export const getResolvedFunnelVersion = (survey) => {
	return getNormalizedSurveyVersion(survey?.version);
};

// a program whose analytics key carries its version - no label anywhere yields the bare key rather
// than a version that was never authored
export const getVersionedFunnelName = (funnelName, version) => {
	const normalizedVersion = getNormalizedSurveyVersion(version);

	return isStringNotEmpty(normalizedVersion) ? `${funnelName}-${normalizedVersion}` : funnelName;
};
